import { ToolsClass } from '@/base/class/tools.class'

export const CARD_ISSUERS = {
  visa: 'VISA',
  mastercard: 'MASTERCARD',
  maestro: 'MAESTRO',
  americanExpress: 'AMERICAN_EXPRESS',
  discover: 'DISCOVER',
  diners: 'DINERS',
  uncategorized: 'UNCATEGORIZED',
}

/**
 * Etapy transakcji kartowej oddawane przez `POST /external/gateway/api/browser/transaction-status`
 * w trybie `clientSource: cardsData` (PEP-354).
 *
 * ⚠️ Wartości pochodzą WYŁĄCZNIE ze swaggera bramki kartowej (`Cards ExternalGateway`) — to on
 * opisuje stan wdrożony. Dokumentacja diagramowa (`cards-flow-diagrams`, V0.80) oraz opis
 * PEP-354 używają nazw krótszych i NIEAKTUALNYCH (`UPDATED`, `GSPA`, `3DS_CHALLENGE`,
 * `3DS_METHOD`) — **nie dokładaj ich tu jako aliasów**. Nieznany etap ma spaść na
 * `CARDS_STAGE_ACTION.wait` i wywołać raport do Sentry, bo ten raport jest jedynym sygnałem, że
 * kontrakt się rozjechał; cichy alias by go zabrał.
 *
 * ⚠️ Lista ma DWANAŚCIE wartości od odczytu 21.09.2026, nie dziewięć jak przy PEP-354 — doszły
 * `DCC_UPDATED`, `GSPA_REJECTED` i `GSPA_RESULT`, a wersja swaggera **została przy `0.0.2`**
 * (spec podmieniony bez podbicia numeru, ten sam scenariusz co przy zniknięciu całego `browser/*`
 * 10.09.2026 — patrz `platnosci.md`). Te trzy etapy przychodzą na ścieżce SZCZĘŚLIWEJ: bramka
 * oddaje je zaraz po przyjęciu `transaction-update` (`DCC_UPDATED`, `GSPA_REJECTED`) oraz po
 * serwerowym wyniku gSPA od Google (`GSPA_RESULT`). Dopóki ich tu nie było, każda przyjęta decyzja
 * DCC i każda przyjęta odmowa gSPA zapalała raport „nieznany etap" — czyli ten, który ma znaczyć
 * „kontrakt się rozjechał". To była realizacja warunku zapisanego wprost w
 * `doc/robocze/pep-800-gspa-plan.md` §3.4 („nie dokładamy `GSPA_RESULT` na zapas; dodamy, gdy
 * okaże się, że wychodzi”).
 * → `doc/dokumentacja/platnosci.md`, „Transakcje kartowe — tryb `cardsData`"
 * @type {Object<string, string>}
 */
export const CARDS_TRANSACTION_STAGE = {
  started: 'STARTED',
  updating: 'UPDATING',
  threeDsMethod: 'THREE_DS_METHOD',
  threeDsMethodResponse: 'THREE_DS_METHOD_RESPONSE',
  threeDsChallenge: 'THREE_DS_CHALLENGE',
  threeDsChallengeResponse: 'THREE_DS_CHALLENGE_RESPONSE',
  gspaStart: 'GSPA_START',
  gspaRejected: 'GSPA_REJECTED',
  gspaResult: 'GSPA_RESULT',
  dccFallback: 'DCC_FALLBACK',
  dccUpdated: 'DCC_UPDATED',
  finished: 'FINISHED',
}

/**
 * Co front ROBI z etapem. Osie są DWIE i nie da się ich zwinąć w jedną: co renderujemy ORAZ czy
 * odpytywanie statusu leci dalej.
 *
 * - `wait`        - czekajka, odpytujemy;
 * - `background`  - ukryta ramka (3DS Method, gSPA), **odpytujemy dalej**;
 * - `interactive` - ekran decyzji użytkownika (DCC), **nie odpytujemy**;
 * - `leave`       - formularz wyprowadzający z dokumentu na ACS (3DS Challenge);
 * - `finish`      - koniec ścieżki: potwierdzenie albo event do SDK.
 *
 * ⚠️ `background` to NIE `interactive`. Przy ukrytej ramce ACS odpowiada **bramce, nie nam**, więc
 * dalsze odpytywanie jest jedynym sposobem dowiedzenia się o zmianie etapu - zatrzymanie go
 * zawiesiłoby transakcję z 3DS Method na zawsze. Odwrotnie przy `interactive`: tam etap nie zmieni
 * się bez kliknięcia użytkownika, więc odpytywanie w tle jest bezcelowe.
 * @type {Object<string, string>}
 */
export const CARDS_STAGE_ACTION = {
  wait: 'wait',
  background: 'background',
  interactive: 'interactive',
  leave: 'leave',
  finish: 'finish',
}

/**
 * Mapa etapu na akcję frontu. Wzięta z diagramów `FlowType BROWSER`, nie z samego enuma - kolejność
 * i zachowanie odpytywania są własnością przepływu, nie nazwy.
 * @type {Object<string, string>}
 */
export const CARDS_STAGE_ACTION_MAP = {
  [CARDS_TRANSACTION_STAGE.started]: CARDS_STAGE_ACTION.wait,
  [CARDS_TRANSACTION_STAGE.updating]: CARDS_STAGE_ACTION.wait,
  [CARDS_TRANSACTION_STAGE.threeDsMethodResponse]: CARDS_STAGE_ACTION.wait,
  [CARDS_TRANSACTION_STAGE.threeDsChallengeResponse]: CARDS_STAGE_ACTION.wait,
  // Trzy etapy „bramka przyjęła i pracuje" - czekajka, odpytujemy dalej. Front nie ma tu nic do
  // zrobienia: `DCC_UPDATED` i `GSPA_REJECTED` są potwierdzeniem naszego `transaction-update`
  // (własny zatrzask już się podniósł), a `GSPA_RESULT` niesie wynik, o którym bramka dowiedziała
  // się od Google SERWEROWO. Żaden z nich nie wchodzi do `CARDS_STAGES_ONCE` - nie mają skutku
  // ubocznego, a czekajki powtarzają się z definicji.
  [CARDS_TRANSACTION_STAGE.dccUpdated]: CARDS_STAGE_ACTION.wait,
  [CARDS_TRANSACTION_STAGE.gspaRejected]: CARDS_STAGE_ACTION.wait,
  [CARDS_TRANSACTION_STAGE.gspaResult]: CARDS_STAGE_ACTION.wait,
  [CARDS_TRANSACTION_STAGE.threeDsMethod]: CARDS_STAGE_ACTION.background,
  [CARDS_TRANSACTION_STAGE.gspaStart]: CARDS_STAGE_ACTION.background,
  [CARDS_TRANSACTION_STAGE.dccFallback]: CARDS_STAGE_ACTION.interactive,
  [CARDS_TRANSACTION_STAGE.threeDsChallenge]: CARDS_STAGE_ACTION.leave,
  [CARDS_TRANSACTION_STAGE.finished]: CARDS_STAGE_ACTION.finish,
}

/**
 * Akcje, po których odpytywanie statusu LECI DALEJ. Nieznany etap zachowuje się jak `wait`, więc
 * też odpytuje - transakcja nie może stanąć na wartości, której nie znamy.
 * @type {string[]}
 */
export const CARDS_STAGE_ACTIONS_POLLING = [CARDS_STAGE_ACTION.wait, CARDS_STAGE_ACTION.background]

/**
 * Etapy wykonywane DOKŁADNIE RAZ na transakcję - jawna LISTA ETAPÓW, nie zbiór akcji.
 *
 * Przedmiotem ochrony jest NIEODWRACALNY SKUTEK UBOCZNY, a nie sama „ważność" etapu:
 * - `THREE_DS_METHOD`    - ukryta ramka SUBMITUJE formularz do ACS, powtórka to drugie żądanie;
 * - `THREE_DS_CHALLENGE` - wyprowadza użytkownika z dokumentu na ACS;
 * - `FINISHED`           - nawigacja na potwierdzenie albo event do SDK.
 *
 * ⚠️ Do PEP-800 lista liczyła się z AKCJI (`background`, `leave`, `finish`) i dlatego obejmowała
 * także `GSPA_START`. To był ten sam błąd, który PEP-806 musiał odkręcić przy `DCC_FALLBACK`:
 * użytkownik, który **odświeżył stronę** na etapie gSPA, wpadał w gałąź „etap powtórzony", ukryta
 * ramka nie montowała się już nigdy, Google nie pytał o zgodę, a pętla dojeżdżała do limitu
 * i kończyła widokiem błędu - za transakcję, którą dało się opłacić.
 *
 * `GSPA_START` tu NIE wchodzi, bo osadzenie adresu jest IDEMPOTENTNE (ten sam adres w tym samym
 * `src` to ta sama ramka, Vue jej nie odtworzy), a strażnikiem jego jedynego ŻĄDANIA jest odesłana
 * odmowa - trwała flaga `spaRejectionSent`, dokładnie jak `dccDecisionSent` przy DCC.
 *
 * ⚠️ Etapy `wait` nie mogą tu wejść NIGDY - powtarzają się w każdej iteracji z definicji.
 * ⚠️ Nie zamieniaj tej listy z powrotem na zbiór akcji „dla symetrii" z `CARDS_STAGE_ACTIONS_POLLING`.
 * Osie są różne: odpytywanie jest własnością AKCJI, a jednorazowość - własnością KONKRETNEGO ETAPU.
 * → `doc/robocze/pep-800-gspa-plan.md` §4.5, `doc/dokumentacja/platnosci.md`
 * @type {string[]}
 */
export const CARDS_STAGES_ONCE = [
  CARDS_TRANSACTION_STAGE.threeDsMethod,
  CARDS_TRANSACTION_STAGE.threeDsChallenge,
  CARDS_TRANSACTION_STAGE.finished,
]

/**
 * Etapy, na których transakcja czeka na CZŁOWIEKA - a nie na bramkę.
 *
 * ⚠️ To TRZECIA oś, niezależna od dwóch poprzednich (co renderujemy, czy odpytujemy) i dotyczy
 * WYŁĄCZNIE budżetu czasu. Nie da się jej wyprowadzić z akcji: `DCC_FALLBACK` czeka na kliknięcie
 * i pętla staje (`interactive`), a `GSPA_START` czeka na prompt na urządzeniu, ale pętla jedzie
 * dalej (`background`) - bo o wyniku bramka dowiaduje się od Google serwerowo. Wspólne mają to,
 * że **czas płynie po stronie użytkownika**, a nie systemu.
 *
 * Na tych etapach budżet ścieżki jest PAUZOWANY, a jego miejsce zajmuje limit lokalny
 * (`INTERACTIVE_DEADLINE_MS` w `cardsTransactionStatus.class.js`).
 * → `doc/robocze/pep-800-gspa-plan.md` §4.11, `doc/dokumentacja/platnosci.md`
 * @type {string[]}
 */
export const CARDS_STAGES_USER_WAIT = [CARDS_TRANSACTION_STAGE.dccFallback, CARDS_TRANSACTION_STAGE.gspaStart]

/**
 * Metoda odpowiadająca na pytanie, czy na tym etapie czekamy na użytkownika.
 *
 * @param {string} stage wartość `data.transactionStage` z odpowiedzi bramki
 * @returns {boolean}
 */
export const isCardsStageWaitingForUser = (stage) => CARDS_STAGES_USER_WAIT.includes(stage)

/**
 * Metoda sprawdzająca, czy etap jest w kontrakcie, który znamy. Rozstrzyga o RAPORCIE, nie
 * o zachowaniu - zachowanie nieznanego etapu daje `resolveCardsStageAction`.
 *
 * ⚠️ Zwykły odczyt z mapy, NIE `Object.hasOwn` - lista polyfilli w `vite.config.mjs`
 * (`plugin-legacy`) jest wyliczona ręcznie i nie ma tam `es.object.has-own`, a `plugin-legacy`
 * transpiluje składnię, nie metody. Na starszym WebKicie byłby to `TypeError` na ścieżce płatności.
 *
 * @param {string} stage wartość `data.transactionStage` z odpowiedzi bramki
 * @returns {boolean}
 */
export const isCardsStageKnown = (stage) => CARDS_STAGE_ACTION_MAP[stage] !== undefined

/**
 * Metoda zamieniająca etap na akcję frontu.
 *
 * ⚠️ Nieznany etap oddaje `wait`, a NIE błąd: nowa albo przemianowana wartość po stronie backendu
 * nie może kosztować opłaconej transakcji. Ścieżkę domknie budżet czasu, a rozjazd kontraktu -
 * raport oparty o `isCardsStageKnown`.
 *
 * @param {string} stage wartość `data.transactionStage` z odpowiedzi bramki
 * @returns {string} jedna z wartości `CARDS_STAGE_ACTION`
 */
export const resolveCardsStageAction = (stage) => CARDS_STAGE_ACTION_MAP[stage] ?? CARDS_STAGE_ACTION.wait

/**
 * Metoda odpowiadająca na pytanie, czy po tym etapie planujemy kolejne odpytanie statusu.
 *
 * @param {string} stage wartość `data.transactionStage` z odpowiedzi bramki
 * @returns {boolean}
 */
export const shouldPollAfterCardsStage = (stage) => CARDS_STAGE_ACTIONS_POLLING.includes(resolveCardsStageAction(stage))

/**
 * Metoda odpowiadająca na pytanie, czy ten etap ma się wykonać tylko raz na transakcję
 * (i trafić do zbioru wykonanych etapów w `sessionStorage`).
 *
 * ⚠️ Pyta o ETAP, nie o jego akcję - patrz `CARDS_STAGES_ONCE`. Nieznany etap nie jest
 * jednorazowy: zachowuje się jak czekajka, a czekajki powtarzają się z definicji.
 *
 * @param {string} stage wartość `data.transactionStage` z odpowiedzi bramki
 * @returns {boolean}
 */
export const isCardsStageExecutedOnce = (stage) => CARDS_STAGES_ONCE.includes(stage)

/* ------------------------------------------------------------------------------------------------
 * Etap `THREE_DS_METHOD` — zbieranie odcisku przeglądarki w ukrytej ramce (PEP-802)
 * ---------------------------------------------------------------------------------------------- */

/** Wymagany prefiks adresu ACS dla formularza 3DS Method. */
const THREE_DS_METHOD_URL_PREFIX = 'https://'

/**
 * Nazwa pola formularza 3DS Method. Wartość wchodzi do `name` inputa, więc jest częścią kontraktu
 * z ACS-em, nie naszą konwencją - wielkość liter jest tu znacząca.
 * @type {string}
 */
export const THREE_DS_METHOD_FIELD = 'threeDSMethodData'

/**
 * Metoda wyjmująca dane formularza 3DS Method z `body.data` odpowiedzi statusu.
 *
 * Kształt ze swaggera bramki (`ThreeDsMethod`: `url`, `threeDSMethodData`) i z opisu PEP-802 - oba
 * mówią to samo, więc czytamy JEDEN kształt: `data['3dsMethod']`.
 *
 * ⚠️ Klucz pola to `3dsMethod` (małe „ds", cyfra z przodu), a **nazwa etapu** to `THREE_DS_METHOD`.
 * To nie jest niekonsekwencja do „naprawienia": przykład w PEP-802 używa dla etapu starej nazwy
 * `3DS_METHOD`, której kontrakt już nie zna - nazwę bierzemy ze swaggera, klucz pola też, a ticket
 * zgadza się z nimi tylko w drugim.
 *
 * ⚠️ Prefiks sprawdzamy ŁAŃCUCHOWO, nie przez `new URL` - `web.url` nie jest na liście polyfilli
 * w `vite.config.mjs`, a `plugin-legacy` transpiluje składnię, nie API (ta sama pułapka co
 * `Object.hasOwn` w `isCardsStageKnown`). Sprawdzenie nie jest kosmetyką: wartość idzie do `action`
 * formularza, który sam się wysyła, więc `javascript:` w tym miejscu to wykonanie kodu w NASZYM
 * dokumencie.
 *
 * ⚠️ Brak cichego odczytu zapasowego - tak samo jak w `getGspaChallengeUrl`. Inny kształt oddaje
 * `null`, czyli tę samą gałąź co brak danych: raport i dalsze odpytywanie. Odczyt „jakkolwiek"
 * zabrałby jedyny sygnał, że bramka oddaje co innego, niż uzgodniono.
 *
 * @param {object|null|undefined} data `body.data` z odpowiedzi `transaction-status`
 * @returns {{url: string, methodData: string}|null} dane formularza albo `null`
 */
export const get3dsMethodData = (data) => {
  const method = data?.['3dsMethod']
  if (method === null || typeof method !== 'object' || Array.isArray(method)) {
    return null
  }
  const url = method.url
  const methodData = method[THREE_DS_METHOD_FIELD]
  if (typeof url !== 'string' || url.length <= THREE_DS_METHOD_URL_PREFIX.length) {
    return null
  }
  if (!url.startsWith(THREE_DS_METHOD_URL_PREFIX)) {
    return null
  }
  if (typeof methodData !== 'string' || methodData.length === 0) {
    return null
  }
  return { url, methodData }
}

/**
 * Metoda podająca NAZWY brakujących albo nieużywalnych pól `3dsMethod`.
 *
 * Oddaje nazwy, a nie samo `false`, bo dokładnie te nazwy idą do raportu w Sentry - i tylko one:
 * ⚠️ **treści tych pól do raportu NIE wolno wysyłać.** `threeDSMethodData` to zakodowany JSON
 * z `threeDSServerTransID` i adresem notyfikacji bramki, czyli identyfikatory, których sanitizer
 * `errorReporting` nie rozpozna po nazwie klucza (ta sama zasada, co „raport z fallbacku REST nie
 * może nieść `data.form`").
 *
 * @param {object|null|undefined} data `body.data` z odpowiedzi `transaction-status`
 * @returns {string[]} nazwy pól do zgłoszenia; pusta tablica = dane komplet
 */
export const getMissing3dsMethodFields = (data) => {
  const method = data?.['3dsMethod']
  if (method === null || typeof method !== 'object' || Array.isArray(method)) {
    return ['3dsMethod']
  }
  const missing = []
  const url = method.url
  if (
    typeof url !== 'string' ||
    url.length <= THREE_DS_METHOD_URL_PREFIX.length ||
    !url.startsWith(THREE_DS_METHOD_URL_PREFIX)
  ) {
    missing.push('url')
  }
  if (typeof method[THREE_DS_METHOD_FIELD] !== 'string' || method[THREE_DS_METHOD_FIELD].length === 0) {
    missing.push(THREE_DS_METHOD_FIELD)
  }
  return missing
}

/* ------------------------------------------------------------------------------------------------
 * Etap `THREE_DS_CHALLENGE` — wyjście z dokumentu na ACS (PEP-803)
 * ---------------------------------------------------------------------------------------------- */

/**
 * Pola formularza wyzwania 3DS, w kolejności z opisu PEP-803 i ze swaggera (`ThreeDsChallenge`).
 *
 * ⚠️ `cReq` jest TWARDE - bez niego ACS nie ma czego przetworzyć, więc formularza nie wysyłamy
 * wcale. Dwa pozostałe są MIĘKKIE: dokładamy je, gdy przyszły, a ich brak daje raport `warning`,
 * nie zatrzymanie przepływu. Nie odwracaj tego „dla pewności": zablokowanie wyjścia na ACS z powodu
 * pola, którego 3DS nie wymaga, kosztowałoby transakcję, którą dało się uwierzytelnić.
 * @type {{hard: string[], soft: string[]}}
 */
export const THREE_DS_CHALLENGE_FIELDS = {
  hard: ['cReq'],
  soft: ['threeDSSessionData', 'threeDsACSRedirectUrl'],
}

/** Wymagany prefiks adresu ACS dla formularza wyzwania. */
const THREE_DS_CHALLENGE_URL_PREFIX = 'https://'

/** @param {*} value @returns {boolean} */
const isUsableFormValue = (value) => typeof value === 'string' && value.length > 0

/**
 * Metoda wyjmująca dane formularza wyzwania 3DS z `body.data` odpowiedzi statusu.
 *
 * ⚠️ **Klucz pola to `3dsChallenge`, a NIE `3dsChallange`.** Opis PEP-803 ma w przykładzie literówkę
 * w obu miejscach naraz (`"transactionStage": "3DS_CHALLANGE"` i `"3dsChallange"`), a kontrakt
 * (`BrowserTransactionStatusResponseData` → `ThreeDsChallenge`) mówi `THREE_DS_CHALLENGE`
 * i `3dsChallenge`. Bierzemy wersję ze swaggera, tak jak przy każdym innym etapie; gdyby bramka
 * oddawała jednak literówkę, powie nam o tym raport „etap 3DS Challenge bez danych formularza"
 * po pierwszym przejeździe, a nie cisza.
 *
 * ⚠️ Prefiks sprawdzamy ŁAŃCUCHOWO, nie przez `new URL` (`web.url` nie jest na liście polyfilli).
 * To nie kosmetyka: wartość idzie do `action` formularza, który sam się wysyła **w głównym oknie**,
 * więc `javascript:` w tym miejscu to wykonanie kodu w naszym dokumencie.
 *
 * @param {object|null|undefined} data `body.data` z odpowiedzi `transaction-status`
 * @returns {{url: string, fields: Array<{name: string, value: string}>, missingSoft: string[]}|null}
 */
export const get3dsChallengeData = (data) => {
  const challenge = data?.['3dsChallenge']
  if (challenge === null || typeof challenge !== 'object' || Array.isArray(challenge)) {
    return null
  }
  const url = challenge.url
  if (
    typeof url !== 'string' ||
    url.length <= THREE_DS_CHALLENGE_URL_PREFIX.length ||
    !url.startsWith(THREE_DS_CHALLENGE_URL_PREFIX)
  ) {
    return null
  }
  if (THREE_DS_CHALLENGE_FIELDS.hard.some((name) => !isUsableFormValue(challenge[name]))) {
    return null
  }

  const fields = THREE_DS_CHALLENGE_FIELDS.hard.map((name) => ({ name, value: challenge[name] }))
  const missingSoft = []
  THREE_DS_CHALLENGE_FIELDS.soft.forEach((name) => {
    if (isUsableFormValue(challenge[name])) {
      fields.push({ name, value: challenge[name] })
    } else {
      missingSoft.push(name)
    }
  })

  return { url, fields, missingSoft }
}

/**
 * Metoda podająca NAZWY brakujących pól twardych wyzwania 3DS.
 *
 * ⚠️ Tylko nazwy - treści tych pól do raportu NIE wolno wysyłać. `cReq` to zakodowane żądanie
 * uwierzytelnienia z identyfikatorami transakcji, a `threeDsACSRedirectUrl` niesie identyfikator
 * płatności w ścieżce (ta sama zasada, co „raport z fallbacku REST nie może nieść `data.form`").
 *
 * @param {object|null|undefined} data `body.data` z odpowiedzi `transaction-status`
 * @returns {string[]} nazwy pól do zgłoszenia; pusta tablica = dane komplet
 */
export const getMissing3dsChallengeFields = (data) => {
  const challenge = data?.['3dsChallenge']
  if (challenge === null || typeof challenge !== 'object' || Array.isArray(challenge)) {
    return ['3dsChallenge']
  }
  const missing = []
  const url = challenge.url
  if (
    typeof url !== 'string' ||
    url.length <= THREE_DS_CHALLENGE_URL_PREFIX.length ||
    !url.startsWith(THREE_DS_CHALLENGE_URL_PREFIX)
  ) {
    missing.push('url')
  }
  THREE_DS_CHALLENGE_FIELDS.hard.forEach((name) => {
    if (!isUsableFormValue(challenge[name])) {
      missing.push(name)
    }
  })
  return missing
}

/* ------------------------------------------------------------------------------------------------
 * Etap `GSPA_START` — Google Secure Payment Authentication w ukrytej ramce (PEP-800)
 * ---------------------------------------------------------------------------------------------- */

/**
 * Treści eventu `window.message`, którymi ramka Google zgłasza wynik uwierzytelnienia.
 *
 * ⚠️ To ŁAŃCUCHY, nie obiekty - `event.data` porównujemy dokładnie. Tak działa dziś stara
 * implementacja PHP na produkcji (`cards-bm-pl`, `common.js`) i tak potwierdził backend
 * (10.09.2026). Wiadomość o innej treści z dozwolonego origin jest IGNOROWANA bez raportu: ramka
 * może wysyłać własną telemetrię, a raport z każdej takiej wiadomości byłby szumem.
 * @type {Object<string, string>}
 */
export const GSPA_EVENT = {
  succeeded: 'SPA_AUTHENTICATION_SUCCEEDED',
  failed: 'SPA_AUTHENTICATION_FAILED',
  cancelled: 'SPA_AUTHENTICATION_CANCELLED',
}

/**
 * Origins, z których PRZYJMUJEMY event wyniku gSPA — produkcyjny i piaskownica Google.
 * @type {string[]}
 */
export const GSPA_ALLOWED_ORIGINS = ['https://pay.google.com', 'https://pay.sandbox.google.com']

/**
 * Origins mocków gSPA na środowiskach testowych (utrzymywane po stronie kart).
 *
 * ⚠️ NIE dopisuj ich do `GSPA_ALLOWED_ORIGINS`. Lista origin to jedyna bramka między
 * `window.message` a żądaniem zmieniającym stan transakcji, więc na produkcji nie ma w niej
 * hostów, których nie kontroluje Google. Stara implementacja PHP trzyma jedną listę dla wszystkich
 * środowisk - tego nie kopiujemy.
 * @type {string[]}
 */
export const GSPA_MOCK_ORIGINS = ['https://cards-mock-accept.bm.pl', 'https://testcards-mock.autopay.eu']

/**
 * Metoda składająca listę dozwolonych origin dla bieżącego środowiska.
 *
 * Poza produkcją dochodzą hosty mockowe, a przy włączonym MSW - własny origin dokumentu, bo strona
 * ramki mockowej (`public/mock/gspa.html`) jest serwowana z dev servera.
 *
 * ⚠️ Funkcja, nie stała: `ToolsClass.isProduction()` i `window.location.origin` czytamy w momencie
 * nasłuchu, a nie przy ładowaniu modułu — inaczej test nie miałby jak podstawić środowiska,
 * a wartość zamarłaby w chunku.
 *
 * @returns {string[]}
 */
export function gspaAllowedOrigins() {
  if (ToolsClass.isProduction()) {
    return [...GSPA_ALLOWED_ORIGINS]
  }
  const origins = [...GSPA_ALLOWED_ORIGINS, ...GSPA_MOCK_ORIGINS]
  if (ToolsClass.isApiMockEnabled() && typeof window?.location?.origin === 'string') {
    origins.push(window.location.origin)
  }
  return origins
}

/**
 * Ile pierwszych odpytań statusu na etapie gSPA leci w rytmie podstawowym (2 s), zanim zwolni.
 *
 * Pierwsze sekundy to realna szansa na wynik BEZ udziału użytkownika (autoryzacja „frictionless"
 * po stronie Google); dalej czekamy na człowieka i drugiego urządzenia nie przyspieszymy.
 * @type {number}
 */
export const GSPA_FAST_POLLS = 3

/**
 * Odstęp między odpytaniami statusu na etapie gSPA po wyczerpaniu `GSPA_FAST_POLLS`.
 *
 * ⚠️ Ta stała istnieje DLATEGO, że pętla na tym etapie NIE staje (`background`), a limit etapu to
 * 30 min: przy stałych 2 s dałoby to ~900 odpytań na transakcję, przy tym rytmie ~180. Nie zmieniaj
 * jej w oderwaniu od limitu etapu i nie „upraszczaj" przez wstrzymanie pętli — powody w planie.
 * → `doc/robocze/pep-800-gspa-plan.md` §4.4
 * @type {number}
 */
export const GSPA_POLL_INTERVAL_MS = 10000

/** Wymagany prefiks adresu wyzwania gSPA. */
const GSPA_URL_PREFIX = 'https://'

/**
 * Metoda wyjmująca adres wyzwania gSPA z `body.data` odpowiedzi statusu.
 *
 * ⚠️ Czytamy WYŁĄCZNIE kształt zagnieżdżony (`data.googleSpa.googleSpaChallengeUrlWithParams`) —
 * potwierdzony przez backend 10.09.2026. Kształt płaski z opisu zadania pochodził z PHP, gdzie
 * adres oddaje INNY endpoint (`google-spa/authentication-start`); tutaj daje `null`, czyli tę samą
 * gałąź co brak adresu: raport i dalsze odpytywanie. Cichy odczyt zapasowy zabrałby jedyny sygnał,
 * że bramka oddaje coś innego, niż uzgodniliśmy.
 *
 * ⚠️ Prefiks sprawdzamy ŁAŃCUCHOWO, nie przez `new URL` - `web.url` nie jest na liście polyfilli
 * w `vite.config.mjs`, a `plugin-legacy` transpiluje składnię, nie API (ta sama pułapka co
 * `Object.hasOwn` w `isCardsStageKnown`). Sprawdzenie nie jest kosmetyką: wartość idzie wprost do
 * `src` ramki, a `javascript:` w tym miejscu to wykonanie kodu w NASZYM dokumencie.
 *
 * @param {object|null|undefined} data `body.data` z odpowiedzi `transaction-status`
 * @returns {string|null} adres albo `null`, gdy go nie ma lub nie nadaje się do `src`
 */
export const getGspaChallengeUrl = (data) => {
  const url = data?.googleSpa?.googleSpaChallengeUrlWithParams
  if (typeof url !== 'string' || url.length <= GSPA_URL_PREFIX.length) {
    return null
  }
  return url.startsWith(GSPA_URL_PREFIX) ? url : null
}

/**
 * Pole i wartość odmowy gSPA w żądaniu `transaction-update` w wariancie `(spa)`.
 *
 * Nazwa pola jest POTWIERDZONA SWAGGEREM (odczyt 21.09.2026): `BrowserTransactionUpdateRequest`
 * deklaruje `gspaState`, nie `spaState` - czyli dokładnie to, co backend ustalił 15.09.2026,
 * wcześniej zgadywane z `dccState` z PEP-806. Wartość `GSPA_REJECTED` nadal pochodzi z diagramów
 * `FlowType BROWSER`, bo swagger opisuje to pole jako zwykły `string` bez enuma; potwierdza ją
 * pośrednio enum ETAPÓW, w którym `GSPA_REJECTED` się pojawiło.
 *
 * ⚠️ Identyfikatory w kodzie zostają przy prefiksie `SPA_` (także klucz magazynu
 * `spaRejectionSent`) - zmiana dotyczy wyłącznie nazwy pola NA DRUCIE.
 *
 * Payload składa JEDNA metoda, a nie trzy miejsca w klasie i komponencie: doprecyzowanie kontraktu
 * ma być zmianą w jednym miejscu. Gdy bramka zacznie rozróżniać powód odmowy
 * (`SPA_AUTHENTICATION_FAILED` vs `..._CANCELLED`), to ta metoda dostanie argument. Swagger nie
 * ogranicza tu wartości, więc rozjazd zgłosi wyłącznie `invalidFields` z odpowiedzi - dlatego
 * nazwy odrzuconych pól idą do raportu.
 * → `doc/robocze/pep-800-gspa-plan.md` §3.3, §12.2 pozycja 1
 * @type {string}
 */
export const SPA_REJECTION_FIELD = 'gspaState'

/** @type {Object<string, string>} */
export const SPA_STATE = {
  rejected: 'GSPA_REJECTED',
}

/**
 * Metoda składająca payload odmowy gSPA (bez identyfikatorów - te dokłada klasa).
 *
 * @returns {Object<string, string>}
 */
export const buildSpaRejectionPayload = () => ({ [SPA_REJECTION_FIELD]: SPA_STATE.rejected })

/**
 * Decyzja użytkownika odsyłana na `transaction-update` w polu `dccState` (PEP-806).
 *
 * Wartości wzięte z widgetu kartowego (`front-widget`, `dcc.mixin.js` → `getDccData()`) - swagger
 * zna już sam endpoint i nazwę pola (odczyt 21.09.2026), ale opisuje `dccState` jako zwykły
 * `string` bez enuma, więc widget zostaje jedynym źródłem prawdy dla WARTOŚCI.
 *
 * ⚠️ Trzecia wartość widgetu, `DCC_NOT_ELIGIBLE`, na tej ścieżce **nie występuje** i nie dokładaj
 * jej tu „na zapas": w widgecie oznacza błąd `check-dcc` PRZED pokazaniem ekranu, a u nas ekran
 * pojawia się dopiero wtedy, gdy bramka sama zdecydowała, że przewalutowanie jest w grze.
 * → `doc/robocze/pep-806-dcc-fallback-plan.md` §2.4
 * @type {Object<string, string>}
 */
export const DCC_STATE = {
  selected: 'DCC_SELECTED',
  declined: 'DCC_DECLINED',
}

/**
 * Pola obiektu `data.dcc`, bez których ekranu wyboru waluty NIE DA SIĘ narysować.
 *
 * ⚠️ Kwoty nie wyliczamy sami (`amount × dccRate`), choćby brakowało tylko `dccAmount`: ekran jest
 * przedmiotem audytu Visy, więc pokazuje liczbę oddaną przez bramkę albo nie pokazuje się wcale.
 * Miękkie są za to `dccMarginPercentage` i `dccExchangeRateSource` (chowamy wiersz + raport
 * `warning`). `dccRequestUuid` NIE jest już nigdzie wysyłane - payload `transaction-update` niesie
 * sam `dccState` (ustalenie z backendem 15.09.2026).
 * @type {string[]}
 */
export const DCC_DATA_REQUIRED_FIELDS = ['amount', 'currency', 'dccAmount', 'dccCurrency', 'dccRate']

/**
 * Metoda sprawdzająca, czy wartość nadaje się na kwotę albo kurs.
 *
 * ⚠️ `Number(null)` to `0`, a `Number('')` też `0` - dlatego puste wartości odpadają PRZED
 * konwersją, a nie po niej. Łańcuch liczbowy przechodzi (JSON bramki oddaje liczby, ale kontrakt
 * tego pola nie deklaruje jako `number` w każdej implementacji).
 *
 * @param {*} value
 * @returns {boolean}
 */
const isUsableAmount = (value) => {
  if (value === null || value === undefined || value === '') {
    return false
  }
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0
}

/** @param {*} value @returns {boolean} */
const isUsableCurrency = (value) => typeof value === 'string' && value.length > 0

/**
 * Metoda podająca NAZWY brakujących pól danych DCC.
 *
 * Oddaje nazwy, a nie samo `true`/`false`, bo dokładnie te nazwy idą do raportu w Sentry - to on
 * jest jedynym sygnałem, że bramka wypuściła etap `DCC_FALLBACK` bez kompletnych danych. Swagger
 * pola `dcc` w odpowiedzi statusu **w ogóle nie zna** (`BrowserTransactionStatusResponseData`
 * deklaruje tylko `transactionStage`, `3dsMethod` i `3dsChallenge` — sprawdzone ponownie
 * 21.09.2026, już po dołożeniu endpointu `transaction-update`), więc ta sytuacja jest realną
 * możliwością, nie hipotezą.
 *
 * @param {object|null|undefined} dcc obiekt `data.dcc` z odpowiedzi `transaction-status`
 * @returns {string[]} nazwy pól, których brakuje; pusta tablica = dane komplet
 */
export const getMissingDccDataFields = (dcc) => {
  if (dcc === null || typeof dcc !== 'object' || Array.isArray(dcc)) {
    return [...DCC_DATA_REQUIRED_FIELDS]
  }
  return DCC_DATA_REQUIRED_FIELDS.filter((field) =>
    field === 'currency' || field === 'dccCurrency' ? !isUsableCurrency(dcc[field]) : !isUsableAmount(dcc[field])
  )
}

/**
 * Metoda odpowiadająca na pytanie, czy z danych DCC da się złożyć ekran wyboru waluty.
 *
 * @param {object|null|undefined} dcc
 * @returns {boolean}
 */
export const isDccDataUsable = (dcc) => getMissingDccDataFields(dcc).length === 0
